import { findRow, formatPrice } from "@/data/pricing";
import { pageFaqAdditions } from "@/data/pageFaqs";
import { createFileRoute } from "@tanstack/react-router";
import {
  absoluteUrl,
  breadcrumbSchema,
  canonicalLinks,
  faqPageSchema,
  jsonLd,
  ogImage,
  ogUrl,
  serviceSchema,
  webPageSchema,
} from "@/lib/seo";
import { LocationPage } from "@/components/site/LocationPage";

const answerPrice = (name: string) => formatPrice(findRow(name));

const PATH = "/fractional-coo-melbourne";
const SERVICE_ID = `${absoluteUrl(PATH)}#service`;
const TITLE = "Fractional COO Melbourne | Ajility";
const DESCRIPTION =
  "Fractional COO support for founder-led Melbourne businesses doing $3M to $30M. Manufacturing, construction, distribution and health.";

const faqs = [
  {
    q: "You are based in Sydney. How does a Melbourne engagement actually work?",
    a: "In blocks. A Melbourne retainer runs as a two-day on-site block every fortnight, usually Tuesday and Wednesday, with the alternate week run remotely to a fixed agenda. The block is booked for the full engagement at kickoff, so it is in calendars months ahead rather than negotiated each time.",
  },
  {
    q: "Is a Melbourne engagement more expensive because of travel?",
    a: "No. Ajility prices deliverables, not time or travel. The Operating Diagnostic is from $3,600 plus GST, the Full Diagnostic and 90-Day Roadmap is from $6,900 plus GST for one entity, and most composed retainers for businesses under $15M start from $3,900 per month plus GST, the same starting price quoted in Sydney. Travel time is absorbed in the price; for interstate work, flights and accommodation are arranged by the client or charged at cost.",
  },
  {
    q: "Do you work with manufacturers?",
    a: "Yes, and Melbourne is where most of that work sits. Food production, plastics, metal fabrication and assembly businesses in the north and south east are a strong fit, particularly where the founder still runs the plant and the commercial side at the same time.",
  },
  {
    q: "Which parts of Victoria do you cover?",
    a: "Metropolitan Melbourne including the west, the north, the south east and the Mornington Peninsula, plus Geelong and Ballarat where the same fortnightly block pattern applies. Regional Victorian businesses further out are usually better served by a monthly on-site block with tighter remote weeks.",
  },
  ...pageFaqAdditions["/fractional-coo-melbourne"],
];

export const Route = createFileRoute("/fractional-coo-melbourne")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Fractional COO and GM Melbourne | Ajility" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Fractional COO and GM Melbourne | Ajility" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Fractional COO in Melbourne for founder-led businesses" },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("fractional-coo-melbourne", "Fractional COO services in Melbourne from Ajility"),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Fractional COO Melbourne", path: PATH }])),
      jsonLd(
        webPageSchema({ image: "fractional-coo-melbourne",
          path: PATH,
          name: TITLE,
          description: DESCRIPTION,
          mainEntityId: SERVICE_ID,
        }),
      ),
      jsonLd({
        ...serviceSchema({
          name: "Fractional COO Melbourne",
          description: DESCRIPTION,
          path: PATH,
          slug: "fractional-coo-melbourne",
          serviceType: "Fractional COO",
          offerId: ["operational-diagnostic", "full-diagnostic-roadmap", "fortnightly-rhythm-base"],
        }),
        areaServed: [
          { "@type": "City", name: "Melbourne" },
          { "@type": "City", name: "Geelong" },
        ],
      }),
      jsonLd(faqPageSchema(PATH, faqs, SERVICE_ID)),
    ],
  }),
  component: MelbournePage,
});

function MelbournePage() {
  return (
    <LocationPage
      city="Melbourne"
      region="VIC"
      heading="Fractional COO in Melbourne"
      lead="Operating leadership for Victorian founders who make, build or move something. Fortnightly on-site blocks, structured remote weeks in between, and the same published pricing as every other city. Most Australian businesses call this seat the general manager; a fractional GM and a fractional COO are the same role."
      intro={[
        "Melbourne has the deepest concentration of founder-led manufacturing and production in the country. The industrial belts through Dandenong South, Braeside, Campbellfield, Laverton and Truganina hold thousands of businesses between $3M and $30M that were started by someone who knew how to make the product and learned the rest as they went. Add the construction and trade base across the growth corridors, the distribution businesses feeding the eastern seaboard, and a large allied health and aged care sector, and the market has a distinctly operational character.",
        "That character shapes the problem. In a services business the constraint is usually people and utilisation. In a Melbourne production or contracting business the constraint is physical: a line that runs at seventy per cent, a yard that cannot sequence work, a quoting model built on last year's input costs. The founder can see the symptom on the floor and still not see it in the numbers, because the numbers arrive six weeks later at a whole-of-company level.",
        "Ajility works the gap between the floor and the P&L. Job or line level costing, a weekly operating scorecard, a leadership team with named ownership, and a quarterly reset that survives contact with a busy month.",
      ]}
      industries={[
        {
          name: "Food and beverage production",
          note: "Melbourne's largest manufacturing employer group. Yield, waste, labour cost per unit and changeover time decide the margin, and in a founder-led plant these are usually known by feel rather than by number. Making them daily and visible is often worth more than any pricing change.",
        },
        {
          name: "Metal fabrication, plastics and assembly",
          note: "Job-shop and short-run manufacturers in the north and south east. The recurring fault is quoting: rates set years ago, no recovery of setup time, and no comparison between the quoted hours and the actual hours. A quote-to-actual review changes the shape of the order book within two quarters.",
        },
        {
          name: "Construction and trade contracting",
          note: "The growth corridors keep civil, concrete, electrical and mechanical sub-contractors busy. Cash is the operating issue as much as margin, with retentions and payment claims tying up more working capital than most founders track deliberately.",
        },
        {
          name: "Distribution and third-party logistics",
          note: "Warehousing and freight businesses in the west and south east serving the eastern seaboard. Cost to serve varies enormously by customer, and almost nobody measures it, so the largest customer is frequently the least profitable one.",
        },
        {
          name: "Allied health, aged care and disability services",
          note: "Multi-site clinician-founded and family-founded providers under funded pricing. When the revenue per hour is capped externally, the only remaining levers are roster efficiency, occupancy, clinical admin time and clean claiming. All four are operating disciplines, not clinical ones.",
        },
      ]}
      sections={[
        {
          heading: "How Melbourne engagements run",
          paragraphs: [
            `Ajility installs operating systems and governance for founder-led Melbourne businesses of $3M to $30M, working on site and remotely. Most clients start with the Operating Diagnostic ${answerPrice("Operating Diagnostic")}. The delivery pattern is deliberately different from Sydney. A Melbourne retainer runs as a two-day on-site block every second week, booked in calendars for the whole engagement at kickoff. The block is built to be dense: the leadership session, the floor or site walk, the monthly commercial review where it falls, and the one-to-one work with the leaders who own the quarterly priorities.`,
            "The alternate week runs remotely to a fixed agenda rather than as a check-in. Scorecard review, action follow-up and decision support on anything that cannot wait a fortnight. Founders sometimes expect the remote week to be the weak one. In practice it is the week that keeps the block honest, because every action agreed on site has a date that falls inside it.",
            "For a fixed-scope install rather than a retainer, such as a governance and meeting rhythm build or a costing model rebuild, the work is compressed into fewer, longer blocks. Geelong and Ballarat run on the same pattern as metropolitan Melbourne. Travel time is absorbed in the price; for interstate work, flights and accommodation are arranged by the client or charged at cost.",
          ],
        },
        {
          heading: "What tends to be wrong in a Victorian production business",
          paragraphs: [
            "The dominant fault is a founder holding two full-time roles. The technical lead who knows the process and the commercial lead who owns the customers are the same person, and neither role gets a full week. The business grows to the ceiling of that person's capacity and stops there.",
            "The second is the reporting lag. Monthly accounts landing three or four weeks after month end cannot govern a plant that makes cost decisions hourly. Installing a weekly operating scorecard with four or five leading indicators changes the conversation from explaining last month to steering this week.",
            "The third is that the plan is not resourced. Melbourne businesses are ambitious about capital expenditure, new lines and new sites, and rarely honest about who is going to run the existing business while all of that happens. Naming an owner and a milestone for every quarterly priority is what stops the growth project from being permanently second in line to today's orders.",
          ],
        },
      ]}
      example={{
        label: "Anonymised Melbourne example",
        title: "A food producer whose best customer was its worst margin",
        body: [
          "A family-founded food production business in Melbourne's south east, a little under $18M in revenue, was busier than it had ever been and no more profitable than it had been three years earlier. The founder had a firm view about which lines made money. The general ledger had no view at all, because everything was reported at company level.",
          "The first block built a cost-to-serve model at customer and line level using existing data rather than a new system. It took three weeks and no capital. The result was uncomfortable: the two highest-volume customers, both won on price and both requiring short-run changeovers, were consuming the margin generated by everyone else.",
          "The response was not to fire customers. It was a renegotiated minimum order quantity, a changeover charge for short runs, and a quoting rate that recovered setup time. Both customers stayed. The weekly scorecard now reports changeover hours and yield by line, so the same problem cannot reappear without being visible inside a week.",
        ],
      }}
      relatedLinks={[
        {
          to: "/services/systems-improvement",
          label: "Systems and Operational Improvement",
          note: "Process and systems work for Melbourne manufacturers, distributors and service businesses carrying too much manual handling.",
        },
        {
          to: "/services/team-structure",
          label: "People, Culture and Accountability",
          note: "Clear roles and decision rights when headcount has grown faster than the structure around it.",
        },
        {
          to: "/scan",
          label: "Operating Scan",
          note: "A free self-assessment that takes about six minutes and returns where your constraint most likely sits.",
        },
      ]}
      cta={{
        heading: "Meet in Melbourne",
        body: "Fit calls are held remotely and Melbourne engagements include regular days on site. The call ends with a plain view of the constraint and a next step with a date on it.",
        linkLabel: "Book a fit call",
      }}
      faqs={faqs}
    />
  );
}
